import { useState } from "react";
import api from "../../../../../utils/axiosInstance";
import { useToast } from "../../../../../components/Toast/ToastProvider";
import { btn } from "../../../../../components/Button/buttonStyles";

const DocumentUpload = ({ vendorId, onClose, onSuccess }) => {
  const [documentName, setDocumentName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const toast = useToast();

  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];

  const handleFileChange = (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (!allowedTypes.includes(selected.type)) {
      toast.error("Invalid file type. Allowed: PNG, JPG, PDF, DOC, DOCX");
      e.target.value = "";
      return;
    }

    setFile(selected);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please select a valid file");
      return;
    }

    const formData = new FormData();
    formData.append("vendor_id", vendorId);
    formData.append("document_name", documentName);
    formData.append("description", description);
    formData.append("file", file);

    try {
      setUploading(true);
      await api.post("/vendor-documents/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      toast.success("Document uploaded successfully");
      onSuccess();
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.response?.data?.message || "Failed to upload document");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col relative mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
            Upload Vendor Document
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <form onSubmit={handleUpload} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">
              Document Name <span className="text-danger-500">*</span>
            </label>
            <input
              type="text"
              className="w-full text-[13px] border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 rounded-lg px-3 py-1.5 outline-none transition"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter document name"
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">
              Description
            </label>
            <textarea
              className="w-full text-[13px] border border-gray-300 focus:border-brand-500 focus:ring-1 focus:ring-brand-500 rounded-lg px-3 py-1.5 outline-none transition"
              rows="3"
              placeholder="Optional description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-gray-600">
              File <span className="text-danger-500">*</span>
            </label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
              onChange={handleFileChange}
              required
              className="block w-full text-sm text-gray-600 border border-gray-300 rounded cursor-pointer focus:border-brand-500 focus:ring-1 focus:ring-brand-500 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-brand-50 file:text-brand-600 file:text-sm hover:file:bg-brand-100 transition"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              Supported: PNG, JPG, PDF, DOC, DOCX (Max 10MB)
            </p>
          </div>

          <div className="pt-2 flex gap-2 text-sm">
            <button
              type="submit"
              disabled={uploading}
              className={btn("primary", "sm")}
            >
              {uploading ? "Uploading..." : "Upload"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className={btn("secondary", "sm")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DocumentUpload;
